import React, { useState, useEffect, useRef } from 'react'
import {
  Timer, CheckCircle, XCircle, ChevronRight,
  RotateCcw, Trophy, AlertTriangle, BookOpen, ChevronDown
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'A2-1'
const PASS_SCORE = 70
const TIME_LIMIT = 25 * 60

interface TestQuestion {
  id: string; number: number; category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string; choices: string[]; answer: number
  explanation: string; concept: string; misconceptionCode: string; learnStepId: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '단항식의 차수', difficulty: 'easy',
    question: '단항식 5x³y²의 차수는?',
    choices: ['2', '3', '5', '6'],
    answer: 3,
    explanation: '차수 = 각 문자 지수의 합 = 3 + 2 = 5\n(5는 계수이므로 제외)',
    concept: '단항식의 차수', misconceptionCode: 'AM01', learnStepId: 'AA01',
  },
  {
    id: 'Q02', number: 2, category: '계수', difficulty: 'easy',
    question: '−3x²y에서 계수는?',
    choices: ['2', '−3', '3', 'x²y'],
    answer: 1,
    explanation: '계수는 문자 앞의 수입니다. −3x²y에서 계수 = −3',
    concept: '단항식의 계수', misconceptionCode: 'AM01', learnStepId: 'AA01',
  },
  {
    id: 'Q03', number: 3, category: '동류항', difficulty: 'easy',
    question: '다음 중 4xy²와 동류항인 것은?',
    choices: ['4xy', '−2xy²', '4x²y', 'xy'],
    answer: 1,
    explanation: '동류항은 문자와 지수가 모두 같아야 합니다.\n4xy²와 −2xy²는 xy² 형태로 동류항입니다.',
    concept: '동류항 판별', misconceptionCode: 'AM01', learnStepId: 'AA01',
  },
  {
    id: 'Q04', number: 4, category: '동류항 계산', difficulty: 'easy',
    question: '7a − 3a + 2a를 계산하면?',
    choices: ['6a', '12a', '4a', '−2a'],
    answer: 0,
    explanation: '(7 − 3 + 2)a = 6a\n동류항끼리 계수를 합산합니다.',
    concept: '동류항 덧셈', misconceptionCode: 'AM02', learnStepId: 'AA02',
  },
  {
    id: 'Q05', number: 5, category: '다항식 덧셈', difficulty: 'easy',
    question: '(3x + 2) + (5x − 4)를 계산하면?',
    choices: ['8x − 2', '8x + 2', '2x − 2', '8x − 6'],
    answer: 0,
    explanation: '3x + 2 + 5x − 4\n= (3 + 5)x + (2 − 4)\n= 8x − 2',
    concept: '다항식의 덧셈', misconceptionCode: 'AM02', learnStepId: 'AA02',
  },
  {
    id: 'Q06', number: 6, category: '다항식 뺄셈', difficulty: 'easy',
    question: '(4x − 3) − (2x + 1)을 계산하면?',
    choices: ['2x − 4', '6x − 2', '2x + 4', '2x − 2'],
    answer: 0,
    explanation: '4x − 3 − 2x − 1\n= (4 − 2)x + (−3 − 1)\n= 2x − 4\n※ 괄호 앞의 − 부호로 뒤 괄호 전체 부호 반전!',
    concept: '다항식의 뺄셈', misconceptionCode: 'AM02', learnStepId: 'AA02',
  },
  {
    id: 'Q07', number: 7, category: '단항식 곱셈', difficulty: 'easy',
    question: '4x² × 3x³을 계산하면?',
    choices: ['7x⁵', '12x⁵', '12x⁶', '7x⁶'],
    answer: 1,
    explanation: '계수: 4 × 3 = 12\n지수: x² × x³ = x^(2+3) = x⁵\n→ 12x⁵',
    concept: '단항식의 곱셈', misconceptionCode: 'AM03', learnStepId: 'AA03',
  },
  {
    id: 'Q08', number: 8, category: '단항식 나눗셈', difficulty: 'medium',
    question: '15x⁵ ÷ 3x²를 계산하면?',
    choices: ['5x³', '5x²', '12x³', '18x³'],
    answer: 0,
    explanation: '계수: 15 ÷ 3 = 5\n지수: x⁵ ÷ x² = x^(5−2) = x³\n→ 5x³',
    concept: '단항식의 나눗셈', misconceptionCode: 'AM03', learnStepId: 'AA03',
  },
  {
    id: 'Q09', number: 9, category: '분배법칙', difficulty: 'medium',
    question: '3x(2x − 5)를 전개하면?',
    choices: ['6x² − 15x', '6x² − 5x', '6x − 15x', '5x² − 15x'],
    answer: 0,
    explanation: '3x × 2x = 6x²\n3x × (−5) = −15x\n→ 6x² − 15x',
    concept: '단항식×다항식 분배법칙', misconceptionCode: 'AM04', learnStepId: 'AA04',
  },
  {
    id: 'Q10', number: 10, category: '대입', difficulty: 'medium',
    question: 'x = 2일 때, 3x² − 2x + 1의 값은?',
    choices: ['9', '11', '13', '17'],
    answer: 0,
    explanation: '3(2)² − 2(2) + 1\n= 3(4) − 4 + 1\n= 12 − 4 + 1\n= 9',
    concept: '다항식에 수 대입', misconceptionCode: 'AM04', learnStepId: 'AA04',
  },
  {
    id: 'Q11', number: 11, category: '거듭제곱', difficulty: 'medium',
    question: '(2x²)³을 계산하면?',
    choices: ['6x⁵', '8x⁵', '6x⁶', '8x⁶'],
    answer: 3,
    explanation: '(2x²)³ = 2³ × (x²)³ = 8 × x⁶ = 8x⁶\n(aⁿ)ᵐ = aⁿᵐ 법칙 적용',
    concept: '거듭제곱', misconceptionCode: 'AM03', learnStepId: 'AA03',
  },
  {
    id: 'Q12', number: 12, category: '복잡한 계산', difficulty: 'medium',
    question: '(2x²y) ÷ (xy) × 3을 계산하면?',
    choices: ['6x', '6xy', '3x', '3xy'],
    answer: 0,
    explanation: '먼저 (2x²y) ÷ (xy) = 2x\n그 다음 2x × 3 = 6x',
    concept: '혼합 계산', misconceptionCode: 'AM03', learnStepId: 'AA03',
  },
  {
    id: 'Q13', number: 13, category: '다항식 정리', difficulty: 'hard',
    question: '(2x² + 3x − 1) − (x² − 2x + 4)를 계산하면?',
    choices: ['x² + 5x − 5', 'x² + x + 3', 'x² + 5x + 3', '3x² + x + 3'],
    answer: 0,
    explanation: '2x² + 3x − 1 − x² + 2x − 4\n= (2−1)x² + (3+2)x + (−1−4)\n= x² + 5x − 5',
    concept: '다항식 뺄셈 종합', misconceptionCode: 'AM02', learnStepId: 'AA02',
  },
  {
    id: 'Q14', number: 14, category: '식 대입', difficulty: 'hard',
    question: 'A = 3x − 1, B = x + 2일 때, 2A − B는?',
    choices: ['5x − 4', '7x', '5x', '7x − 4'],
    answer: 0,
    explanation: '2A = 2(3x − 1) = 6x − 2\nB = x + 2\n2A − B = 6x − 2 − x − 2 = 5x − 4',
    concept: '식의 대입', misconceptionCode: 'AM04', learnStepId: 'AA04',
  },
  {
    id: 'Q15', number: 15, category: '종합', difficulty: 'hard',
    question: '(−2ab)² ÷ 4a를 계산하면?',
    choices: ['ab²', '−ab²', 'ab', '4a²b²'],
    answer: 0,
    explanation: '(−2ab)² = 4a²b²\n4a²b² ÷ 4a = ab²',
    concept: '거듭제곱+나눗셈 종합', misconceptionCode: 'AM03', learnStepId: 'AA03',
  },
]

type Phase = 'ready' | 'testing' | 'done'

export default function A2_1TestPage({ onGoLearn }: { onGoLearn?: (stepId: string) => void }) {
  const [phase, setPhase]     = useState<Phase>('ready')
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT)
  const [current, setCurrent] = useState(0)
  const [showExp, setShowExp] = useState<Record<number, boolean>>({})
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))

  useEffect(() => {
    if (phase === 'testing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { clearInterval(timerRef.current!); finishTest(); return 0 }
          return t - 1
        })
      }, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [phase])

  const finishTest = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const seconds = TIME_LIMIT - timeLeft
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer)
      .map(q => q.misconceptionCode)
      .filter((v, i, arr) => arr.indexOf(v) === i)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID, score: sc, correctCount: correct,
      totalCount: TEST_QUESTIONS.length, durationSec: seconds,
      answers: answers.map(a => a ?? -1),
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('done')
  }

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
  const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correct / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const diffColor = { easy: 'bg-emerald-100 text-emerald-700', medium: 'bg-amber-100 text-amber-700', hard: 'bg-red-100 text-red-700' }
  const diffLabel = { easy: '쉬움', medium: '보통', hard: '어려움' }

  if (phase === 'ready') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-6 mb-6 text-center">
          <p className="text-4xl mb-3">📐</p>
          <h2 className="text-2xl font-bold mb-2">단항식과 다항식 실전 TEST</h2>
          <p className="text-indigo-100 text-sm">A2-1 단원 전체 평가</p>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { icon: '📝', label: '총 문항', value: `${TEST_QUESTIONS.length}문항` },
            { icon: '⏱️', label: '제한 시간', value: '25분' },
            { icon: '🎯', label: '합격 기준', value: `${PASS_SCORE}점 이상` },
            { icon: '📊', label: '난이도', value: '쉬움~어려움' },
          ].map(item => (
            <div key={item.label} className="card border border-slate-200 text-center">
              <p className="text-2xl mb-1">{item.icon}</p>
              <p className="text-xs text-slate-400">{item.label}</p>
              <p className="text-sm font-bold text-slate-800">{item.value}</p>
            </div>
          ))}
        </div>
        <div className="card border border-slate-200 mb-6">
          <h3 className="font-bold text-slate-800 mb-3">📋 출제 범위</h3>
          <div className="space-y-2">
            {[
              { step: 'AA01', title: '단항식의 차수·계수·동류항', count: 3, color: 'bg-sky-100 text-sky-700' },
              { step: 'AA02', title: '다항식의 덧셈·뺄셈', count: 4, color: 'bg-emerald-100 text-emerald-700' },
              { step: 'AA03', title: '단항식의 곱셈·나눗셈·거듭제곱', count: 5, color: 'bg-orange-100 text-orange-700' },
              { step: 'AA04', title: '분배법칙·대입·종합', count: 3, color: 'bg-violet-100 text-violet-700' },
            ].map(item => (
              <div key={item.step} className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${item.color}`}>{item.step}</span>
                <span className="text-sm text-slate-700 flex-1">{item.title}</span>
                <span className="text-xs text-slate-400">{item.count}문제</span>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => { setPhase('testing'); setTimeLeft(TIME_LIMIT) }}
          className="w-full py-4 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white rounded-2xl font-bold text-lg transition-all shadow-lg"
        >
          시험 시작하기 🚀
        </button>
      </div>
    )
  }

  if (phase === 'testing') {
    const q = TEST_QUESTIONS[current]
    const answered = answers.filter(a => a !== null).length
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700">{current + 1}/{TEST_QUESTIONS.length}</span>
            <div className="h-1.5 w-32 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
            </div>
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${timeLeft < 300 ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-700'}`}>
            <Timer size={14} />{fmt(timeLeft)}
          </div>
        </div>

        <div className="card border border-slate-200 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">Q{q.number}</span>
            <span className="text-xs text-slate-500">{q.category}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ml-auto ${diffColor[q.difficulty]}`}>{diffLabel[q.difficulty]}</span>
          </div>
          <p className="text-sm font-semibold text-slate-800 mb-4 whitespace-pre-line">{q.question}</p>
          <div className="space-y-2">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${answers[current] === i ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'}`}
              >
                <span className="font-bold mr-2">{['①', '②', '③', '④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setCurrent(c => Math.max(0, c - 1))} disabled={current === 0}
            className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 flex items-center justify-center gap-1">
              다음 <ChevronRight size={14} />
            </button>
          ) : (
            <button onClick={finishTest} disabled={answered < TEST_QUESTIONS.length}
              className="flex-1 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400">
              제출하기 ({answered}/{TEST_QUESTIONS.length})
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${i === current ? 'bg-indigo-600 text-white' : answers[i] !== null ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <div className={`rounded-2xl p-6 mb-5 text-center text-white bg-gradient-to-br ${passed ? 'from-emerald-500 to-teal-600' : 'from-slate-600 to-slate-700'}`}>
        <p className="text-5xl mb-2">{passed ? '🏆' : '📚'}</p>
        <p className="text-3xl font-black mb-1">{score}점</p>
        <p className="text-lg font-bold opacity-90">{passed ? '합격! 단항식·다항식 마스터!' : '조금 더 연습이 필요해요!'}</p>
        <p className="text-sm opacity-75 mt-1">{correct}/{TEST_QUESTIONS.length} 정답 · {passed ? '✅ 합격' : `❌ 불합격 (기준 ${PASS_SCORE}점)`}</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { icon: '📐', label: '차수·계수', steps: ['AA01'] },
          { icon: '➕', label: '다항식 계산', steps: ['AA02'] },
          { icon: '✖️', label: '곱셈·나눗셈', steps: ['AA03', 'AA04'] },
        ].map(group => {
          const groupQs = TEST_QUESTIONS.filter(q => group.steps.includes(q.learnStepId))
          const groupCorrect = groupQs.filter(q => {
            const idx = TEST_QUESTIONS.findIndex(tq => tq.id === q.id)
            return answers[idx] === q.answer
          }).length
          const pct = groupQs.length > 0 ? Math.round((groupCorrect / groupQs.length) * 100) : 0
          return (
            <div key={group.label} className="card border border-slate-200 text-center">
              <p className="text-xl mb-1">{group.icon}</p>
              <p className="text-xs font-bold text-slate-700">{group.label}</p>
              <p className={`text-sm font-black mt-0.5 ${pct >= 70 ? 'text-emerald-600' : 'text-red-500'}`}>{pct}%</p>
            </div>
          )
        })}
      </div>

      <div className="card border border-slate-200 mb-5">
        <h3 className="font-bold text-slate-800 mb-3">📋 문항별 결과</h3>
        <div className="space-y-2">
          {TEST_QUESTIONS.map((q, i) => {
            const isCorrect = answers[i] === q.answer
            return (
              <div key={q.id}>
                <button onClick={() => setShowExp(s => ({ ...s, [i]: !s[i] }))}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  {isCorrect ? <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" /> : <XCircle size={16} className="text-red-500 flex-shrink-0" />}
                  <span className="text-sm font-semibold text-slate-700 flex-1 text-left">Q{q.number}. {q.category}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${diffColor[q.difficulty]}`}>{diffLabel[q.difficulty]}</span>
                  <ChevronDown size={13} className={`text-slate-400 transition-transform ${showExp[i] ? 'rotate-180' : ''}`} />
                </button>
                {showExp[i] && (
                  <div className="mt-1 p-3 bg-white border border-slate-200 rounded-xl text-xs space-y-1.5 ml-2">
                    <p className="text-slate-700 font-medium whitespace-pre-line">{q.question}</p>
                    {!isCorrect && <p className="text-red-600">내 답: {answers[i] !== null ? q.choices[answers[i]!] : '미응답'}</p>}
                    <p className="text-emerald-700 font-semibold">정답: {q.choices[q.answer]}</p>
                    <p className="text-slate-500 whitespace-pre-line">{q.explanation}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {!passed && (
        <div className="card border border-amber-200 bg-amber-50 mb-5">
          <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2"><AlertTriangle size={16} /> 취약 개념 안내</h3>
          {TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.misconceptionCode)
            .filter((v, i, arr) => arr.indexOf(v) === i)
            .map(code => (
              <div key={code} className="flex items-start gap-2 mb-2">
                <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-mono font-bold flex-shrink-0 mt-0.5">{code}</span>
                <p className="text-xs text-amber-700">
                  {code === 'AM01' && '단항식 차수·계수·동류항 혼동 → AA01 개념 재학습 권장'}
                  {code === 'AM02' && '다항식 덧셈·뺄셈 부호 오류 → AA02 개념 재학습 권장'}
                  {code === 'AM03' && '지수 법칙(곱셈·나눗셈) 오류 → AA03 개념 재학습 권장'}
                  {code === 'AM04' && '분배법칙·대입 오류 → AA04 개념 재학습 권장'}
                </p>
              </div>
            ))}
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><BookOpen size={11} /> 개념 학습 탭에서 해당 단계를 다시 학습해보세요!</p>
          {onGoLearn && (
            <div className="mt-2 space-y-1">
              {TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer)
                .filter((q, i, arr) => arr.findIndex(x => x.learnStepId === q.learnStepId) === i)
                .map(q => (
                  <button key={q.learnStepId} onClick={() => onGoLearn(q.learnStepId)}
                    className="w-full text-left text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 font-medium flex items-center gap-1.5">
                    <BookOpen size={11} /> {q.learnStepId} 개념 다시 학습하기 →
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <button onClick={() => { setPhase('ready'); setAnswers(Array(TEST_QUESTIONS.length).fill(null)); setTimeLeft(TIME_LIMIT); setCurrent(0); setShowExp({}) }}
          className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50 flex items-center justify-center gap-2">
          <RotateCcw size={15} /> 다시 풀기
        </button>
        {passed && (
          <div className="flex-1 py-3 rounded-2xl bg-emerald-500 text-white font-bold text-sm flex items-center justify-center gap-2">
            <Trophy size={15} /> 단항식·다항식 완료! 🎉
          </div>
        )}
      </div>
    </div>
  )
}
